import { LegacyAnalyticsEventData } from '@scentregroup/shared/analytics'
import { IEcommerceProduct } from '@scentregroup/shared/analytics/types/events/legacy'
import {
  affiliations,
  events,
} from '@scentregroup/shared/helpers/analytics-utils/constants'
import makeUniqueProductId from '@scentregroup/shared/helpers/make-unique-product-id'
import { IMarketplaceCartItem } from '../types/cart'
import {
  IMarketplaceProductVariant,
  MarketplaceProduct,
} from '../types/marketplace-product'

function makeProductFromCartItem(
  cartItem: IMarketplaceCartItem,
  adjustedQuantity: number
): IEcommerceProduct & { quantity: number } {
  const eligiblePromotion = cartItem.variant.promotions?.[0]

  return {
    id: cartItem.variantGroupCode || cartItem.sku,
    name: cartItem.name,
    uniqueId: makeUniqueProductId(cartItem.id, cartItem.shop.id, cartItem.sku),
    price: cartItem.discountPrice || cartItem.listPrice,
    brand: cartItem.brand,
    category: cartItem.category?.path
      .map(pathSegment => pathSegment.title)
      .join(' / '),
    variant: cartItem.optionValues?.map(o => o.value).join('|'),
    quantity: Math.abs(cartItem.quantity - adjustedQuantity),
    dimension127: cartItem.shop.name,
    dimension128: cartItem.shop.retailer?.sys.id,
    dimension129: 'AUD',
    ...(eligiblePromotion && {
      dimension131: eligiblePromotion.campaignId,
      dimension132: eligiblePromotion.title,
    }),
    dimension136: cartItem.variantGroupCode,
    dimension137: cartItem.sku,
    dimension138: cartItem.shop.id,
  }
}

export function makeAnalyticsCartItemUpdatedEventData(
  cartId: string,
  cartItem: IMarketplaceCartItem,
  adjustedQuantity: number
): LegacyAnalyticsEventData | undefined {
  if (cartItem.quantity === adjustedQuantity) {
    return
  }

  const product = makeProductFromCartItem(cartItem, adjustedQuantity)

  if (cartItem.quantity > adjustedQuantity) {
    return {
      event: events.REMOVE_FROM_CART,
      cartId,
      ecommerce: {
        currencyCode: 'AUD',
        affiliation: affiliations.WESTFIELD_DIRECT_AU,
        remove: {
          products: [product],
        },
      },
    }
  } else {
    return {
      event: events.ADD_TO_CART,
      cartId,
      ecommerce: {
        currencyCode: 'AUD',
        affiliation: affiliations.WESTFIELD_DIRECT_AU,
        add: {
          products: [product],
        },
      },
    }
  }
}

export function makeAnalyticsProductRemovedEventData(
  cartItem: IMarketplaceCartItem,
  cartId: string
): LegacyAnalyticsEventData {
  return {
    event: events.REMOVE_FROM_CART,
    cartId,
    ecommerce: {
      currencyCode: 'AUD',
      affiliation: affiliations.WESTFIELD_DIRECT_AU,
      remove: {
        products: [makeProductFromCartItem(cartItem, 0)],
      },
    },
  }
}

export function makeAnalyticsProductAddedData(
  product: MarketplaceProduct,
  selectedVariant: IMarketplaceProductVariant,
  quantity: number,
  cartId: string
): LegacyAnalyticsEventData {
  const eligiblePromotion = selectedVariant?.promotions?.[0]

  return {
    event: events.ADD_TO_CART,
    cartId,
    ecommerce: {
      currencyCode: 'AUD',
      affiliation: affiliations.WESTFIELD_DIRECT_AU,
      add: {
        products: [
          {
            id: selectedVariant.variantGroupCode || selectedVariant.sku,
            name: product.title,
            uniqueId: makeUniqueProductId(
              selectedVariant.id,
              product.shop.id,
              selectedVariant.sku
            ),
            price:
              selectedVariant.discountPrice || selectedVariant.originalPrice,
            brand: product.brand,
            category: product.category?.path
              .map(pathSegment => pathSegment.title)
              .join(' / '),
            variant: selectedVariant.optionValues.map(o => o.value).join('|'),
            quantity,
            dimension127: product.shop.name,
            dimension128: product.shop.retailerId,
            dimension129: 'AUD',
            ...(eligiblePromotion && {
              dimension131: eligiblePromotion.campaignId,
              dimension132: eligiblePromotion.title,
            }),
            dimension136: selectedVariant.variantGroupCode,
            dimension137: selectedVariant.sku,
            dimension138: product.shop.id,
          },
        ],
      },
    },
  }
}
